import type {PostRefundCreateProcedureParams} from 'domain/usecases/private/refund/request/post-create-procedure';
import {makePostRefundCreateProcedure} from 'main/factories/usecases/private/refund/request/post-create-procedure';
import {OperationStateTypesEnum, QueryParamsEnum} from 'presentation/constants';
import {useCallback, useState} from 'react';
import {useTherapiesDataStore} from '../store/useTherapiesDataStore';
import type {TherapyDataSchema} from '../components/AddTherapy/schemas/therapyDataSchema';
import type {TherapyData} from '../store/useTherapiesDataStore/types';
import {useQueryParams} from 'presentation/pages/private/hooks';

export const useTherapies = () => {
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const therapies = useTherapiesDataStore(state => state.therapies);
	const editingTherapy = useTherapiesDataStore(state => state.editingTherapy);
	const lastSequentialProcedure = useTherapiesDataStore(state => state.lastSequentialProcedure);
	const incrementLastSequentialProcedure = useTherapiesDataStore(
		state => state.incrementLastSequentialProcedure,
	);
	const resetLastSequentialProcedure = useTherapiesDataStore(
		state => state.resetLastSequentialProcedure,
	);
	const addTherapy = useTherapiesDataStore(state => state.addTherapy);
	const removeTherapy = useTherapiesDataStore(state => state.removeTherapy);
	const resetTherapies = useTherapiesDataStore(state => state.resetTherapies);
	const moveTherapyToEdit = useTherapiesDataStore(state => state.moveTherapyToEdit);
	const restoreTherapyFromEdit = useTherapiesDataStore(state => state.restoreTherapyFromEdit);
	const updateTherapyFromEdit = useTherapiesDataStore(state => state.updateTherapyFromEdit);

	const modalEditDataAndCleanProcedures = useTherapiesDataStore(
		state => state.modalEditDataAndCleanProcedures,
	);
	const openModalEditDataAndCleanProcedures = useTherapiesDataStore(
		state => state.openModalEditDataAndCleanProcedures,
	);
	const closeModalEditDataAndCleanProcedures = useTherapiesDataStore(
		state => state.closeModalEditDataAndCleanProcedures,
	);

	const [operationState, setOperationState] = useState(OperationStateTypesEnum.NONE);

	const isEditMode = !!editingTherapy;

	const isLoadingProcedures = operationState === OperationStateTypesEnum.SUBMITTING;

	const isEmptyTherapies = therapies.length <= 0;

	const isListPageMode = !['add', 'edit'].includes(SEARCH_PARAMS.refundTherapyMode);
	const isAddPageMode = SEARCH_PARAMS.refundTherapyMode === 'add';
	const isEditPageMode = SEARCH_PARAMS.refundTherapyMode === 'edit';
	const isAddOrEditPageMode = isAddPageMode || isEditPageMode;

	const handleGoToTherapyMode = (mode?: 'add' | 'edit' | 'list') => {
		setSearchParams(
			prev => {
				if (!mode || mode === 'list') {
					prev.delete(QueryParamsEnum.REFUND_THERAPY_MODE);
				} else {
					prev.set(QueryParamsEnum.REFUND_THERAPY_MODE, mode);
				}
				return prev;
			},
			{
				replace: true,
			},
		);
	};

	async function handleCreateProcedure(params: PostRefundCreateProcedureParams) {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		try {
			const response = await makePostRefundCreateProcedure().post(params);
			setOperationState(OperationStateTypesEnum.DONE);
			return response.content;
		} catch (e) {
			setOperationState(OperationStateTypesEnum.ERROR);
			return null;
		}
	}

	const handleAddTherapy = useCallback(
		(newTherapy: TherapyDataSchema) => {
			addTherapy(newTherapy);
		},
		[addTherapy],
	);

	const handleLoadTherapyForEdit = useCallback(
		(therapyIndex: number) => {
			moveTherapyToEdit(therapyIndex);
			handleGoToTherapyMode('edit');
		},
		[moveTherapyToEdit, handleGoToTherapyMode],
	);

	const handleCancelTherapyEdit = useCallback(() => {
		restoreTherapyFromEdit();
	}, [restoreTherapyFromEdit]);

	const handleSaveTherapyEdit = useCallback(
		(therapyData: TherapyData) => {
			updateTherapyFromEdit(therapyData);
		},
		[updateTherapyFromEdit],
	);

	return {
		operationState,
		therapies,
		editingTherapy,
		lastSequentialProcedure,
		isLoadingProcedures,
		isEmptyTherapies,
		isListPageMode,
		isAddPageMode,
		isEditPageMode,
		isAddOrEditPageMode,
		isEditMode,
		modalEditDataAndCleanProcedures,
		removeTherapy,
		resetTherapies,
		handleCreateProcedure,
		handleAddTherapy,
		handleLoadTherapyForEdit,
		handleCancelTherapyEdit,
		handleSaveTherapyEdit,
		incrementLastSequentialProcedure,
		resetLastSequentialProcedure,
		handleGoToTherapyMode,
		openModalEditDataAndCleanProcedures,
		closeModalEditDataAndCleanProcedures,
	};
};
